/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from "react";
import "./EbayAutomationPage.css";

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const BASE_URL =
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const POLL_INTERVAL_MS = 4000;

const STATUS_CONFIG = {
  queued: { label: "Queued", color: "#6b7280", bg: "#f3f4f6" },
  processing: { label: "Processing", color: "#d97706", bg: "#fef3c7" },
  listed: { label: "Listed", color: "#059669", bg: "#d1fae5" },
  failed: { label: "Failed", color: "#dc2626", bg: "#fee2e2" },
  skipped: { label: "Skipped", color: "#7c3aed", bg: "#ede9fe" },
  reversed: { label: "Reversed", color: "#0369a1", bg: "#e0f2fe" },
};

const BATCH_STATUS_CONFIG = {
  running: { label: "Running", color: "#d97706", bg: "#fef3c7" },
  completed: { label: "Completed", color: "#059669", bg: "#d1fae5" },
  partial: { label: "Partial", color: "#ea580c", bg: "#ffedd5" },
  failed: { label: "Failed", color: "#dc2626", bg: "#fee2e2" },
  reversed: { label: "Reversed", color: "#0369a1", bg: "#e0f2fe" },
};

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const getFromStorage = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
};

const getUserId = () => {
  const user = getFromStorage("user");
  return user.id || user.user_id || user.userId || null;
};

const getCompanyId = () => localStorage.getItem("company_id");

const formatDate = (str) => {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (startStr, endStr) => {
  if (!startStr || !endStr) return null;
  const secs = Math.round((new Date(endStr) - new Date(startStr)) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status, type = "product" }) => {
  const cfg =
    type === "batch" ? BATCH_STATUS_CONFIG[status] : STATUS_CONFIG[status];
  if (!cfg) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 20,
        fontSize: "0.72rem",
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}33`,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  );
};

const ProgressBar = ({ value, color = "#2563eb" }) => (
  <div
    style={{
      background: "#e5e7eb",
      borderRadius: 8,
      height: 8,
      overflow: "hidden",
      flexGrow: 1,
    }}
  >
    <div
      style={{
        width: `${Math.min(100, value)}%`,
        height: "100%",
        background: color,
        borderRadius: 8,
        transition: "width 0.5s ease",
      }}
    />
  </div>
);

const Spinner = ({ size = 16 }) => (
  <span
    style={{
      display: "inline-block",
      width: size,
      height: size,
      border: `2px solid #e5e7eb`,
      borderTop: `2px solid #2563eb`,
      borderRadius: "50%",
      animation: "ea-spin 0.7s linear infinite",
    }}
  />
);

// ─────────────────────────────────────────────────────────────
// STORE ASSIGNMENT PANEL
// ─────────────────────────────────────────────────────────────
const StoreAssignmentPanel = ({
  stores,
  totalProducts,
  onAssignmentsChange,
}) => {
  // Each store entry: { store_id, count }
  const [storeSlots, setStoreSlots] = useState(() =>
    stores.map((s) => ({
      store_id: s.id,
      store_name: s.storeName,
      marketplace: s.marketplace,
      count: 0,
    })),
  );

  const totalAssigned = storeSlots.reduce((sum, s) => sum + s.count, 0);
  const remaining = totalProducts - totalAssigned;

  const handleCountChange = (storeId, rawVal) => {
    const val = Math.max(0, parseInt(rawVal) || 0);
    setStoreSlots((prev) => {
      const updated = prev.map((s) =>
        s.store_id === storeId ? { ...s, count: val } : s,
      );
      // Propagate
      onAssignmentsChange(updated);
      return updated;
    });
  };

  // Distribute evenly button
  const distributeEvenly = () => {
    if (!stores.length) return;
    const perStore = Math.floor(totalProducts / stores.length);
    const remainder = totalProducts % stores.length;
    const updated = storeSlots.map((s, i) => ({
      ...s,
      count: perStore + (i === 0 ? remainder : 0),
    }));
    setStoreSlots(updated);
    onAssignmentsChange(updated);
  };

  return (
    <div className="ea-store-panel">
      <div className="ea-store-panel-header">
        <div>
          <h3>Assign to eBay Stores</h3>
          <p>
            {totalAssigned} of {totalProducts} products assigned
          </p>
        </div>
        <button className="ea-btn-ghost" onClick={distributeEvenly}>
          Distribute evenly
        </button>
      </div>

      {remaining !== 0 && (
        <div className={`ea-assign-warning ${remaining < 0 ? "over" : ""}`}>
          {remaining > 0
            ? `⚠ ${remaining} product${remaining !== 1 ? "s" : ""} not assigned to any store`
            : `⚠ Over-assigned by ${Math.abs(remaining)} — reduce store counts`}
        </div>
      )}

      <div className="ea-store-list">
        {storeSlots.map((slot) => {
          const store = stores.find((s) => s.id === slot.store_id);
          const hasPolicies =
            store?.shipping_policy_id ||
            store?.payment_policy_id ||
            store?.return_policy_id;

          return (
            <div key={slot.store_id} className="ea-store-row">
              <div className="ea-store-info">
                <span className="ea-store-name">{slot.store_name}</span>
                <span className="ea-store-market">{slot.marketplace}</span>
                {!hasPolicies && (
                  <span className="ea-store-warn">⚠ No policies saved</span>
                )}
              </div>
              <div className="ea-store-count-wrap">
                <button
                  className="ea-count-btn"
                  onClick={() =>
                    handleCountChange(slot.store_id, slot.count - 1)
                  }
                >
                  −
                </button>
                <input
                  type="number"
                  className="ea-count-input"
                  value={slot.count}
                  min={0}
                  max={totalProducts}
                  onChange={(e) =>
                    handleCountChange(slot.store_id, e.target.value)
                  }
                />
                <button
                  className="ea-count-btn"
                  onClick={() =>
                    handleCountChange(slot.store_id, slot.count + 1)
                  }
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// LIVE BATCH MONITOR
// ─────────────────────────────────────────────────────────────
const BatchMonitor = ({ batchId, companyId, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedLogIds, setSelectedLogIds] = useState(new Set());
  const [reversing, setReversing] = useState(false);
  const [reverseResult, setReverseResult] = useState(null);
  const [confirmType, setConfirmType] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [recoverResult, setRecoverResult] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/automation/status/${batchId}?company_id=${companyId}`,
      );
      const json = await res.json();
      if (json.success) {
        setData(json);
        // Stop polling when done (including reversed)
        if (
          ["completed", "partial", "failed", "reversed"].includes(
            json.batch.status,
          )
        ) {
          clearInterval(pollRef.current);
        }
      } else {
        setError(json.error || "Failed to fetch status");
      }
    } catch (err) {
      setError(err.message);
    }
  }, [batchId, companyId]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  // ── Toggle single log selection ──────────────────────────
  const toggleLog = (id) => {
    setSelectedLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Toggle select all listed logs ────────────────────────
  const listedLogs = data?.logs?.filter((l) => l.status === "listed") || [];
  const allListedSelected =
    listedLogs.length > 0 && listedLogs.every((l) => selectedLogIds.has(l.id));

  const toggleAllListed = () => {
    if (allListedSelected) {
      setSelectedLogIds(new Set());
    } else {
      setSelectedLogIds(new Set(listedLogs.map((l) => l.id)));
    }
  };

  // ── Execute reverse ───────────────────────────────────────
  // ── Recover stuck batch ──────────────────────────────────
  const executeRecover = async () => {
    setRecovering(true);
    setRecoverResult(null);
    try {
      const res = await fetch(`${BASE_URL}/api/automation/recover/${batchId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company_id: companyId }),
      });
      const json = await res.json();
      setRecoverResult(json);
      if (json.success) await fetchStatus();
    } catch (err) {
      setRecoverResult({ success: false, error: err.message });
    } finally {
      setRecovering(false);
    }
  };

  const executeReverse = async () => {
    setReversing(true);
    setReverseResult(null);
    setConfirmType(null);
    try {
      let res, json;
      if (confirmType === "batch") {
        res = await fetch(
          `${BASE_URL}/api/automation/reverse/batch/${batchId}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ company_id: companyId }),
          },
        );
      } else {
        res = await fetch(`${BASE_URL}/api/automation/reverse/products`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            company_id: companyId,
            log_ids: Array.from(selectedLogIds),
          }),
        });
      }
      json = await res.json();
      setReverseResult(json);
      if (json.success) {
        setSelectedLogIds(new Set());
        // Refresh data
        await fetchStatus();
      }
    } catch (err) {
      setReverseResult({ success: false, error: err.message });
    } finally {
      setReversing(false);
    }
  };

  if (error) {
    return (
      <div className="ea-monitor">
        <div className="ea-monitor-header">
          <h3>Batch #{batchId}</h3>
          <button className="ea-btn-ghost" onClick={onClose}>
            ← Back
          </button>
        </div>
        <div className="ea-error">{error}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ea-monitor">
        <div className="ea-monitor-header">
          <h3>Loading batch #{batchId}…</h3>
          <button className="ea-btn-ghost" onClick={onClose}>
            ← Back
          </button>
        </div>
        <div className="ea-loading-center">
          <Spinner size={28} />
        </div>
      </div>
    );
  }

  const { batch, logs } = data;
  const isRunning = batch.status === "running";
  const isStuck =
    isRunning &&
    !recovering &&
    new Date() - new Date(batch.updated_at) > 10 * 60 * 1000; // > 10 min no update
  const isReversed = batch.status === "reversed";
  const canReverse = !isRunning && !isReversed && !reversing;

  const listed = logs.filter((l) => l.status === "listed").length;
  const failed = logs.filter((l) => l.status === "failed").length;
  const queued = logs.filter((l) => l.status === "queued").length;
  const processing = logs.filter((l) => l.status === "processing").length;
  const reversedCount = logs.filter((l) => l.status === "reversed").length;

  const progressColor =
    batch.status === "failed"
      ? "#dc2626"
      : batch.status === "partial"
        ? "#ea580c"
        : batch.status === "reversed"
          ? "#0369a1"
          : "#2563eb";

  return (
    <div className="ea-monitor">
      {/* Confirm dialog */}
      {confirmType && (
        <div className="ea-confirm-overlay">
          <div className="ea-confirm-box">
            <h4>⚠ Confirm Reverse</h4>
            <p>
              {confirmType === "batch"
                ? `This will delete ALL ${listed} listed product(s) from eBay and remove them from the database. SmartScan items will be reset to pending.`
                : `This will delete ${selectedLogIds.size} selected product(s) from eBay and remove them from the database. SmartScan items will be reset to pending.`}
            </p>
            <p style={{ color: "#dc2626", fontWeight: 600, fontSize: "13px" }}>
              This cannot be undone.
            </p>
            <div className="ea-confirm-actions">
              <button
                className="ea-btn-ghost"
                onClick={() => setConfirmType(null)}
                disabled={reversing}
              >
                Cancel
              </button>
              <button
                className="ea-btn-danger"
                onClick={executeReverse}
                disabled={reversing}
              >
                {reversing ? (
                  <>
                    <Spinner size={14} /> Reversing…
                  </>
                ) : (
                  "Yes, Reverse"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stuck batch warning banner */}
      {isStuck && (
        <div className="ea-stuck-banner">
          <div>
            <strong>⚠ Batch appears stuck</strong>
            <p>
              No progress in over 10 minutes — likely caused by a server
              restart. Click Recover to mark unprocessed products as failed and
              reset them to pending so they can be re-run.
            </p>
          </div>
          <button
            className="ea-btn-recover"
            onClick={executeRecover}
            disabled={recovering}
          >
            {recovering ? (
              <>
                <Spinner size={14} /> Recovering…
              </>
            ) : (
              "↺ Recover Batch"
            )}
          </button>
        </div>
      )}

      {/* Recover result banner */}
      {recoverResult && (
        <div
          className={`ea-reverse-banner ${recoverResult.success ? "success" : "error"}`}
        >
          {recoverResult.success
            ? `✅ ${recoverResult.message}`
            : `❌ Recovery failed: ${recoverResult.error}`}
          <button
            onClick={() => setRecoverResult(null)}
            className="ea-banner-close"
          >
            ×
          </button>
        </div>
      )}

      {/* Reverse result banner */}
      {reverseResult && (
        <div
          className={`ea-reverse-banner ${reverseResult.success ? "success" : "error"}`}
        >
          {reverseResult.success
            ? `✅ Reversed ${reverseResult.succeeded} product(s) successfully. ${reverseResult.failed ? `${reverseResult.failed} failed.` : ""} SmartScan items reset to pending.`
            : `❌ Reverse failed: ${reverseResult.error}`}
          <button
            onClick={() => setReverseResult(null)}
            className="ea-banner-close"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="ea-monitor-header">
        <div>
          <h3>{batch.batch_name || `Batch #${batchId}`}</h3>
          <span className="ea-meta">
            {batch.listing_type === "FIXED_PRICE"
              ? "🏷️ Fixed Price"
              : "🔨 Auction"}{" "}
            · Started {formatDate(batch.created_at)}
            {batch.completed_at &&
              ` · Finished ${formatDate(batch.completed_at)}`}
          </span>
        </div>
        <div className="ea-monitor-header-right">
          <StatusBadge status={batch.status} type="batch" />
          {/* Reverse selected */}
          {canReverse && selectedLogIds.size > 0 && (
            <button
              className="ea-btn-danger-outline"
              onClick={() => setConfirmType("selected")}
              disabled={reversing}
            >
              ↩ Reverse Selected ({selectedLogIds.size})
            </button>
          )}
          {/* Reverse entire batch */}
          {canReverse && listed > 0 && (
            <button
              className="ea-btn-danger"
              onClick={() => setConfirmType("batch")}
              disabled={reversing}
            >
              ↩ Reverse Entire Batch
            </button>
          )}
          {/* Recover stuck batch */}
          {isRunning && (
            <button
              className="ea-btn-ghost"
              onClick={executeRecover}
              disabled={recovering}
              title="Use if batch is stuck due to server restart"
              style={{ color: "#d97706", borderColor: "#d97706" }}
            >
              {recovering ? (
                <>
                  <Spinner size={13} /> Recovering…
                </>
              ) : (
                "↺ Recover"
              )}
            </button>
          )}
          <button className="ea-btn-ghost" onClick={onClose}>
            ← Back
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="ea-progress-section">
        <div className="ea-progress-row">
          {isRunning && <Spinner size={14} />}
          <span className="ea-progress-label">
            {batch.done} / {batch.products_total} products processed
          </span>
          <span className="ea-progress-pct">{batch.progress}%</span>
        </div>
        <ProgressBar value={batch.progress} color={progressColor} />
        <div className="ea-counter-row">
          <span className="ea-counter listed">{listed} Listed</span>
          <span className="ea-counter failed">{failed} Failed</span>
          {reversedCount > 0 && (
            <span className="ea-counter reversed">
              {reversedCount} Reversed
            </span>
          )}
          {queued + processing > 0 && (
            <span className="ea-counter queued">
              {queued + processing} Remaining
            </span>
          )}
        </div>
      </div>

      {/* Log table */}
      <div className="ea-log-table-wrap">
        <table className="ea-log-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Product</th>
              <th>Store</th>
              <th>SKU</th>
              <th>Price</th>
              <th>Listing ID</th>
              <th>Attempts</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr
                key={log.id}
                className={`ea-log-row ea-log-row--${log.status}${selectedLogIds.has(log.id) ? " ea-log-row--selected" : ""}`}
              >
                {canReverse && listed > 0 && (
                  <td style={{ width: 32 }}>
                    {log.status === "listed" && (
                      <input
                        type="checkbox"
                        checked={selectedLogIds.has(log.id)}
                        onChange={() => toggleLog(log.id)}
                      />
                    )}
                  </td>
                )}
                <td>
                  {log.first_image ? (
                    <img
                      src={log.first_image}
                      alt=""
                      className="ea-thumb"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="ea-thumb-empty" />
                  )}
                </td>
                <td>
                  <div className="ea-product-name">
                    {log.display_title || "—"}
                  </div>
                  {log.size && (
                    <div className="ea-product-sub">Size: {log.size}</div>
                  )}
                </td>
                <td>
                  <div className="ea-store-cell">
                    <span>{log.store_name || "—"}</span>
                    {log.marketplace && (
                      <span className="ea-market-tag">{log.marketplace}</span>
                    )}
                  </div>
                </td>
                <td>
                  <code className="ea-code">{log.sku || "—"}</code>
                </td>
                <td>
                  {log.price_listed
                    ? `${log.currency || ""} ${parseFloat(log.price_listed).toFixed(2)}`
                    : "—"}
                </td>
                <td>
                  {log.ebay_listing_id ? (
                    <a
                      href={`https://www.ebay.co.uk/itm/${log.ebay_listing_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="ea-link"
                    >
                      {log.ebay_listing_id}
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="ea-center">{log.attempts || 0}</td>
                <td className="ea-center">
                  {formatDuration(log.started_at, log.completed_at) ||
                    (log.status === "processing" ? <Spinner size={12} /> : "—")}
                </td>
                <td>
                  <StatusBadge status={log.status} />
                </td>
                <td>
                  {log.error_message ? (
                    <span className="ea-error-cell" title={log.error_message}>
                      {log.error_message.substring(0, 60)}
                      {log.error_message.length > 60 ? "…" : ""}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PAST BATCHES LIST
// ─────────────────────────────────────────────────────────────
const PastBatches = ({ companyId, onViewBatch }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/automation/batches?company_id=${companyId}`,
        );
        const json = await res.json();
        if (json.success) setBatches(json.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchBatches();
  }, [companyId]);

  if (loading)
    return (
      <div className="ea-loading-center">
        <Spinner size={24} />
      </div>
    );
  if (!batches.length) {
    return (
      <div className="ea-empty">
        <p>No automation batches yet. Start your first batch above.</p>
      </div>
    );
  }

  return (
    <div className="ea-past-batches">
      <h3 className="ea-section-title">Past Batches</h3>
      <div className="ea-batch-table-wrap">
        <table className="ea-batch-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Batch Name</th>
              <th>Started</th>
              <th>Total</th>
              <th>Listed</th>
              <th>Failed</th>
              <th>Success Rate</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} className="ea-batch-row">
                <td className="ea-center">
                  <code className="ea-code">{b.id}</code>
                </td>
                <td>{b.batch_name || `Batch #${b.id}`}</td>
                <td>{formatDate(b.created_at)}</td>
                <td className="ea-center">{b.products_total}</td>
                <td className="ea-center ea-listed">{b.products_listed}</td>
                <td className="ea-center ea-failed">{b.products_failed}</td>
                <td>
                  <div className="ea-rate-wrap">
                    <ProgressBar
                      value={b.success_rate || 0}
                      color={
                        b.success_rate >= 80
                          ? "#059669"
                          : b.success_rate >= 50
                            ? "#d97706"
                            : "#dc2626"
                      }
                    />
                    <span className="ea-rate-pct">{b.success_rate || 0}%</span>
                  </div>
                </td>
                <td>
                  <StatusBadge status={b.status} type="batch" />
                </td>
                <td>
                  <button
                    className="ea-btn-sm"
                    onClick={() => onViewBatch(b.id)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
const EbayAutomationPage = () => {
  const companyId = getCompanyId();
  const userId = getUserId();

  // ── Data state ───────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingStores, setLoadingStores] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // ── Selection ────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── Store assignment: storeSlots[i] = { store_id, count } ─
  const [storeAssignments, setStoreAssignments] = useState([]);

  // ── Batch name ───────────────────────────────────────────
  const [batchName, setBatchName] = useState("");

  // ── Listing type — editor chooses per batch ───────────────
  const [listingType, setListingType] = useState("AUCTION"); // "AUCTION" | "FIXED_PRICE"

  // ── Submission ───────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [activeBatchId, setActiveBatchId] = useState(null);

  // ── Active tab ───────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("new"); // "new" | "history"

  // ── Filter / search ──────────────────────────────────────
  const [search, setSearch] = useState("");

  // ── Seller filter ─────────────────────────────────────────
  const [selectedSeller, setSelectedSeller] = useState("ALL"); // "ALL" | user_id string

  // ─────────────────────────────────────────────────────────
  // Fetch products
  // ─────────────────────────────────────────────────────────
  const fetchProducts = useCallback(async () => {
    if (!companyId) return;
    setLoadingProducts(true);
    setFetchError(null);
    try {
      const res = await fetch(
        `${BASE_URL}/api/automation/products?company_id=${companyId}`,
      );
      const json = await res.json();
      if (json.success) {
        setProducts(json.data);
      } else {
        setFetchError(json.error || "Failed to load products");
      }
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setLoadingProducts(false);
    }
  }, [companyId]);

  // ─────────────────────────────────────────────────────────
  // Fetch stores
  // ─────────────────────────────────────────────────────────
  const fetchStores = useCallback(async () => {
    if (!companyId) return;
    setLoadingStores(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/automation/stores?company_id=${companyId}`,
      );
      const json = await res.json();
      if (json.success) setStores(json.data);
    } catch (err) {
      console.error("Stores fetch failed:", err);
    } finally {
      setLoadingStores(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchProducts();
    fetchStores();
  }, [fetchProducts, fetchStores]);

  // ─────────────────────────────────────────────────────────
  // Selection helpers
  // ─────────────────────────────────────────────────────────
  // Unique sellers from product list for the dropdown
  const uniqueSellers = Array.from(
    new Map(
      products
        .filter((p) => p.user_id && p.seller_name)
        .map((p) => [p.user_id, { id: p.user_id, name: p.seller_name }]),
    ).values(),
  ).sort((a, b) => a.name.localeCompare(b.name));

  const filteredProducts = products.filter((p) => {
    // Seller filter
    if (
      selectedSeller !== "ALL" &&
      String(p.user_id) !== String(selectedSeller)
    ) {
      return false;
    }
    // Search filter
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (p.title || "").toLowerCase().includes(q) ||
      (p.seo_title || "").toLowerCase().includes(q) ||
      (p.brand || "").toLowerCase().includes(q) ||
      (p.sku || "").toLowerCase().includes(q) ||
      (p.seller_name || "").toLowerCase().includes(q)
    );
  });

  // Group filtered products by seller for grouped view
  const groupedBySeller = filteredProducts.reduce((groups, p) => {
    const key = p.seller_name || "Unknown Seller";
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
    return groups;
  }, {});

  const allSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((p) => selectedIds.has(p.id));

  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selectedIds);
      filteredProducts.forEach((p) => next.delete(p.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      filteredProducts.forEach((p) => next.add(p.id));
      setSelectedIds(next);
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  // ─────────────────────────────────────────────────────────
  // Build assignments from selection + store slots
  // ─────────────────────────────────────────────────────────
  const buildAssignments = () => {
    const selectedList = products.filter((p) => selectedIds.has(p.id));
    const assignments = [];
    let cursor = 0;

    for (const slot of storeAssignments) {
      if (!slot.count) continue;
      const batch = selectedList.slice(cursor, cursor + slot.count);
      for (const product of batch) {
        assignments.push({
          smartscan_id: product.id,
          ebay_store_id: slot.store_id,
        });
      }
      cursor += slot.count;
    }

    // Any remaining (not assigned to a store) are skipped
    return assignments;
  };

  const totalAssigned = storeAssignments.reduce((s, sl) => s + sl.count, 0);
  const canStart =
    selectedIds.size > 0 &&
    stores.length > 0 &&
    totalAssigned > 0 &&
    totalAssigned === selectedIds.size &&
    !submitting;

  // ─────────────────────────────────────────────────────────
  // Start batch
  // ─────────────────────────────────────────────────────────
  const handleStart = async () => {
    if (!canStart) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const assignments = buildAssignments();

      if (!assignments.length) {
        setSubmitError("No valid assignments — check your store counts.");
        return;
      }

      const res = await fetch(`${BASE_URL}/api/automation/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company_id: companyId,
          editor_id: userId,
          batch_name:
            batchName.trim() || `Batch ${new Date().toLocaleString()}`,
          listing_type: listingType,
          assignments,
        }),
      });

      const json = await res.json();

      if (!json.success) {
        setSubmitError(json.error || "Failed to start batch");
        return;
      }

      // Navigate to monitor view
      setActiveBatchId(json.batch_id);

      // Clear selection
      setSelectedIds(new Set());
      setBatchName("");
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // If viewing a batch monitor
  // ─────────────────────────────────────────────────────────
  if (activeBatchId) {
    return (
      <div className="ea-page">
        <BatchMonitor
          batchId={activeBatchId}
          companyId={companyId}
          onClose={() => {
            setActiveBatchId(null);
            fetchProducts(); // refresh pending list
          }}
        />
      </div>
    );
  }

  const isLoading = loadingProducts || loadingStores;

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <div className="ea-page">
      {/* Page header */}
      <div className="ea-header">
        <div>
          <h1 className="ea-page-title">eBay Automation</h1>
          <p className="ea-page-sub">
            Select pending products, assign to eBay stores, and list them all in
            one click.
          </p>
        </div>
        <div className="ea-header-actions">
          <button
            className="ea-btn-ghost"
            onClick={fetchProducts}
            disabled={isLoading}
          >
            {loadingProducts ? <Spinner size={14} /> : "↺"} Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="ea-tabs">
        <button
          className={`ea-tab ${activeTab === "new" ? "active" : ""}`}
          onClick={() => setActiveTab("new")}
        >
          New Batch
          {products.length > 0 && (
            <span className="ea-tab-badge">{products.length}</span>
          )}
        </button>
        <button
          className={`ea-tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          Batch History
        </button>
      </div>

      {/* ── TAB: NEW BATCH ── */}
      {activeTab === "new" && (
        <div className="ea-new-batch">
          {fetchError && (
            <div className="ea-error ea-error--banner">{fetchError}</div>
          )}

          {isLoading ? (
            <div className="ea-loading-center">
              <Spinner size={32} />
              <p>Loading products and stores…</p>
            </div>
          ) : (
            <div className="ea-split">
              {/* LEFT: Product selection */}
              <div className="ea-left">
                <div className="ea-panel-header">
                  <div>
                    <h3>Pending Products</h3>
                    <p>
                      {selectedIds.size} of {filteredProducts.length} selected
                      {selectedSeller !== "ALL" && (
                        <span className="ea-filter-active"> · filtered</span>
                      )}
                    </p>
                  </div>
                  <div className="ea-filter-row">
                    {/* Seller filter dropdown */}
                    <select
                      className="ea-select"
                      value={selectedSeller}
                      onChange={(e) => {
                        setSelectedSeller(e.target.value);
                        setSelectedIds(new Set()); // clear selection on filter change
                      }}
                    >
                      <option value="ALL">
                        All Sellers ({products.length})
                      </option>
                      {uniqueSellers.map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {s.name} (
                          {
                            products.filter(
                              (p) => String(p.user_id) === String(s.id),
                            ).length
                          }
                          )
                        </option>
                      ))}
                    </select>
                    {/* Search */}
                    <input
                      type="text"
                      className="ea-search"
                      placeholder="Search by title, brand, SKU…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {/* Clear filters */}
                    {(selectedSeller !== "ALL" || search) && (
                      <button
                        className="ea-btn-ghost"
                        onClick={() => {
                          setSelectedSeller("ALL");
                          setSearch("");
                        }}
                        title="Clear filters"
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>
                </div>

                {filteredProducts.length === 0 ? (
                  <div className="ea-empty">
                    {search
                      ? "No products match your search."
                      : "No pending products. All SmartScan products have been processed."}
                  </div>
                ) : (
                  <div className="ea-product-table-wrap">
                    <table className="ea-product-table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={toggleAll}
                              title="Select all visible"
                            />
                          </th>
                          <th>Image</th>
                          <th>Product</th>
                          <th>Brand</th>
                          <th>Size</th>
                          <th>Condition</th>
                          <th>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(groupedBySeller).map(
                          ([sellerName, sellerProducts]) => {
                            // Per-seller select all
                            const sellerAllSelected = sellerProducts.every(
                              (p) => selectedIds.has(p.id),
                            );
                            const sellerSomeSelected = sellerProducts.some(
                              (p) => selectedIds.has(p.id),
                            );

                            const toggleSellerAll = (e) => {
                              e.stopPropagation();
                              const next = new Set(selectedIds);
                              if (sellerAllSelected) {
                                sellerProducts.forEach((p) =>
                                  next.delete(p.id),
                                );
                              } else {
                                sellerProducts.forEach((p) => next.add(p.id));
                              }
                              setSelectedIds(next);
                            };

                            return (
                              <React.Fragment key={sellerName}>
                                {/* ── Seller group header row ── */}
                                <tr className="ea-seller-group-row">
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={sellerAllSelected}
                                      ref={(el) => {
                                        if (el)
                                          el.indeterminate =
                                            sellerSomeSelected &&
                                            !sellerAllSelected;
                                      }}
                                      onChange={toggleSellerAll}
                                      title={`Select all from ${sellerName}`}
                                    />
                                  </td>
                                  <td colSpan={6}>
                                    <div className="ea-seller-group-label">
                                      <span className="ea-seller-avatar">
                                        {sellerName.charAt(0).toUpperCase()}
                                      </span>
                                      <strong>{sellerName}</strong>
                                      <span className="ea-seller-count">
                                        {
                                          sellerProducts.filter((p) =>
                                            selectedIds.has(p.id),
                                          ).length
                                        }{" "}
                                        / {sellerProducts.length} selected
                                      </span>
                                    </div>
                                  </td>
                                </tr>

                                {/* ── Products for this seller ── */}
                                {sellerProducts.map((p) => {
                                  const isSelected = selectedIds.has(p.id);
                                  const score =
                                    parseFloat(p.condition_score) || 0;
                                  const condLabel =
                                    score >= 10
                                      ? "New"
                                      : score >= 9
                                        ? "Like New"
                                        : score >= 8
                                          ? "Excellent"
                                          : score >= 7
                                            ? "Good"
                                            : score >= 6
                                              ? "Fair"
                                              : "Poor";

                                  return (
                                    <tr
                                      key={p.id}
                                      className={`ea-product-row ${isSelected ? "selected" : ""}`}
                                      onClick={() => toggleOne(p.id)}
                                    >
                                      <td onClick={(e) => e.stopPropagation()}>
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => toggleOne(p.id)}
                                        />
                                      </td>
                                      <td>
                                        {p.images?.[0] ? (
                                          <img
                                            src={p.images[0]}
                                            alt=""
                                            className="ea-thumb"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        ) : (
                                          <div className="ea-thumb-empty" />
                                        )}
                                      </td>
                                      <td>
                                        <div className="ea-product-name">
                                          {p.seo_title || p.title || "Untitled"}
                                        </div>
                                        {p.category && (
                                          <div className="ea-product-sub">
                                            {p.category}
                                          </div>
                                        )}
                                      </td>
                                      <td>{p.brand || "—"}</td>
                                      <td>{p.size || "—"}</td>
                                      <td>
                                        <span
                                          style={{
                                            fontSize: "0.72rem",
                                            fontWeight: 600,
                                            color:
                                              score >= 8
                                                ? "#059669"
                                                : score >= 6
                                                  ? "#d97706"
                                                  : "#dc2626",
                                          }}
                                        >
                                          {condLabel} ({score}/10)
                                        </span>
                                      </td>
                                      <td>
                                        {p.price_recommended
                                          ? `${p.currency || ""} ${parseFloat(p.price_recommended).toFixed(2)}`
                                          : "—"}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          },
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* RIGHT: Configuration */}
              <div className="ea-right">
                {/* Batch name */}
                <div className="ea-config-section">
                  <label className="ea-label">Batch Name (optional)</label>
                  <input
                    type="text"
                    className="ea-input"
                    placeholder={`Batch ${new Date().toLocaleDateString()}`}
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                  />
                </div>

                {/* Listing type — editor's choice, applies to whole batch */}
                <div className="ea-config-section">
                  <label className="ea-label">Listing Type</label>
                  <div className="ea-listing-type-toggle">
                    <button
                      type="button"
                      className={`ea-toggle-btn ${listingType === "AUCTION" ? "active" : ""}`}
                      onClick={() => setListingType("AUCTION")}
                    >
                      🔨 Auction
                      <span className="ea-toggle-sub">7 day duration</span>
                    </button>
                    <button
                      type="button"
                      className={`ea-toggle-btn ${listingType === "FIXED_PRICE" ? "active" : ""}`}
                      onClick={() => setListingType("FIXED_PRICE")}
                    >
                      🏷️ Fixed Price
                      <span className="ea-toggle-sub">Good 'Til Cancelled</span>
                    </button>
                  </div>
                  <p className="ea-field-hint">
                    Applies to every product in this batch.
                  </p>
                </div>

                {/* Store assignment */}
                {stores.length === 0 ? (
                  <div className="ea-error">
                    No eBay stores connected. Please connect a store first.
                  </div>
                ) : (
                  <StoreAssignmentPanel
                    stores={stores}
                    totalProducts={selectedIds.size}
                    onAssignmentsChange={setStoreAssignments}
                  />
                )}

                {/* Summary */}
                <div className="ea-summary">
                  <div className="ea-summary-row">
                    <span>Products selected</span>
                    <strong>{selectedIds.size}</strong>
                  </div>
                  <div className="ea-summary-row">
                    <span>Total assigned</span>
                    <strong
                      style={{
                        color:
                          totalAssigned === selectedIds.size
                            ? "#059669"
                            : "#dc2626",
                      }}
                    >
                      {totalAssigned}
                    </strong>
                  </div>
                  <div className="ea-summary-row">
                    <span>Listing type</span>
                    <strong>
                      {listingType === "AUCTION"
                        ? "🔨 Auction (7 days)"
                        : "🏷️ Fixed Price (GTC)"}
                    </strong>
                  </div>
                </div>

                {submitError && (
                  <div className="ea-error ea-error--banner">{submitError}</div>
                )}

                {/* Start button */}
                <button
                  className="ea-btn-start"
                  onClick={handleStart}
                  disabled={!canStart}
                  title={
                    selectedIds.size === 0
                      ? "Select at least one product"
                      : totalAssigned !== selectedIds.size
                        ? "Assign all selected products to stores"
                        : undefined
                  }
                >
                  {submitting ? (
                    <>
                      <Spinner size={16} /> Starting…
                    </>
                  ) : (
                    <>▶ Start Automation ({selectedIds.size} products)</>
                  )}
                </button>

                <p className="ea-start-hint">
                  Products will be listed on eBay as{" "}
                  {listingType === "AUCTION"
                    ? "7-day auctions"
                    : "fixed price (GTC) listings"}{" "}
                  using saved store policies. Retries: up to 3 per product.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: HISTORY ── */}
      {activeTab === "history" && (
        <PastBatches
          companyId={companyId}
          onViewBatch={(id) => setActiveBatchId(id)}
        />
      )}
    </div>
  );
};

export default EbayAutomationPage;
